// Super Admin → "Schema groups".
//
// Each schema is one doctor; a clinic with several doctors spans several
// schemas. This screen assigns each schema a clinic label in the
// `Schema: Group` form the request described — one row per schema, with a
// free-text group box and a datalist of labels already in use so an admin
// picks the existing spelling instead of inventing a near-duplicate.
//
// Writes `public.schema_settings."Group"` straight from the browser with the
// anon key, the same way the Fax Numbers modal writes `fax_num` — that table's
// read policy is USING (true) and the Super Admin page is already password
// gated. See utils/schemaGroups.js for why the column is read via select("*").
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { supabasePublic } from "./supabaseClient";
import {
  GROUP_COLUMN,
  groupsFromRows,
  normalizeGroup,
  rowsToSchemaGroups,
} from "../utils/schemaGroups";

// Sentinel for the "By group" filter. A real group key is a lowercased label,
// so a leading " " can never collide with one.
const UNGROUPED_KEY = " ungrouped";

const SchemaGroupAdminSettings = ({ isOpen, onClose }) => {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("*")
        .order("schema_name", { ascending: true });
      if (error) throw error;
      setRows(rowsToSchemaGroups(data));
    } catch (err) {
      // The most likely cause by far is the migration not having been run yet,
      // so say that rather than surfacing a bare PostgREST string.
      setLoadError(err.message || String(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const setGroup = (schemaName, value) =>
    setRows((prev) =>
      prev.map((r) => (r.schema_name === schemaName ? { ...r, group: value } : r))
    );

  // Preview of what the grouping will look like, recomputed as they type — the
  // point of this screen is the grouping, not the individual rows.
  const groups = useMemo(() => groupsFromRows(rows), [rows]);
  const ungrouped = useMemo(
    () => rows.filter((r) => !String(r.group || "").trim()).map((r) => r.schema_name),
    [rows]
  );
  // Existing labels feed the datalist so admins reuse a spelling.
  const knownGroupNames = useMemo(() => groups.map((g) => g.name), [groups]);

  // "By group" tab: which group is being inspected. "" = every group,
  // UNGROUPED_KEY = the schemas with no label at all.
  const [filterKey, setFilterKey] = useState("");
  // Schemas matching the current filter, as [{ label, schemas }] so the
  // "all groups" case can render every group under its own heading.
  const filtered = useMemo(() => {
    if (filterKey === UNGROUPED_KEY) {
      return ungrouped.length ? [{ label: "Ungrouped", schemas: ungrouped }] : [];
    }
    if (!filterKey) {
      return [
        ...groups.map((g) => ({ label: g.name, schemas: g.schemas })),
        ...(ungrouped.length ? [{ label: "Ungrouped", schemas: ungrouped }] : []),
      ];
    }
    const hit = groups.find((g) => g.key === filterKey);
    return hit ? [{ label: hit.name, schemas: hit.schemas }] : [];
  }, [filterKey, groups, ungrouped]);

  const handleSave = async () => {
    setSaving(true);
    const failures = [];
    for (const row of rows) {
      try {
        const { error } = await supabasePublic()
          .from("schema_settings")
          .update({ [GROUP_COLUMN]: normalizeGroup(row.group) })
          .eq("schema_name", row.schema_name);
        if (error) throw error;
      } catch (err) {
        failures.push(`${row.schema_name}: ${err.message}`);
      }
    }
    setSaving(false);
    if (failures.length) {
      toast({
        title: "Some schemas didn't save",
        description: failures.join("; "),
        status: "error",
        duration: 8000,
        isClosable: true,
      });
      return;
    }
    toast({
      title: "Schema groups saved",
      description: `${groups.length} group${groups.length === 1 ? "" : "s"} across ${
        rows.length - ungrouped.length
      } schema${rows.length - ungrouped.length === 1 ? "" : "s"}.`,
      status: "success",
      duration: 4000,
      isClosable: true,
    });
    if (onClose) onClose();
  };

  if (loading) {
    return (
      <Box py={6} textAlign="center">
        <Spinner />
        <Text mt={2} color="gray.500">
          Loading schemas…
        </Text>
      </Box>
    );
  }

  if (loadError) {
    return (
      <VStack align="start" spacing={2}>
        <Text color="red.600" fontSize="sm">
          Couldn't load schema groups: {loadError}
        </Text>
        <Text color="gray.600" fontSize="sm">
          If this mentions a missing <code>Group</code> column, run{" "}
          <code>sql/migrations/2026-07-31-schema-settings-group.sql</code> first.
        </Text>
        <Button size="sm" onClick={load}>
          Retry
        </Button>
      </VStack>
    );
  }

  if (!rows.length) {
    return (
      <Text color="gray.600" fontSize="sm">
        No rows in <code>schema_settings</code>.
      </Text>
    );
  }

  return (
    <VStack spacing={0} align="stretch" w="100%" minH={0}>
      <Tabs variant="enclosed" size="sm" isLazy>
        <TabList>
          <Tab>Assign</Tab>
          <Tab>By group</Tab>
        </TabList>
        <TabPanels>
          {/* ── Assign: one editable row per schema ───────────────────── */}
          <TabPanel px={0}>
            <Box flexShrink={0} pb={3}>
              <Text fontSize="sm" color="gray.600">
                Each schema is one doctor. Give the schemas of doctors in the same
                clinic the same group name — a patient approved into a group is added
                to every member's patient roster. Leave blank for a standalone schema.
              </Text>
            </Box>

            <Box maxH={{ base: "40vh", md: "45vh" }} overflowY="auto" pr={2}>
              <Table size="sm" variant="simple">
                <Thead position="sticky" top={0} bg="white" zIndex={1}>
                  <Tr>
                    <Th>Schema</Th>
                    <Th>Group</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((row) => (
                    <Tr key={row.schema_name}>
                      <Td fontWeight="semibold" whiteSpace="nowrap">
                        {row.schema_name}
                      </Td>
                      <Td>
                        <Input
                          size="sm"
                          list="schema-group-names"
                          value={row.group || ""}
                          placeholder="ungrouped"
                          onChange={(e) => setGroup(row.schema_name, e.target.value)}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              {/* Shared across every row so a group typed once is offered everywhere. */}
              <datalist id="schema-group-names">
                {knownGroupNames.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </Box>
          </TabPanel>

          {/* ── By group: pick a clinic, see who's in it ───────────────── */}
          <TabPanel px={0}>
            <Box flexShrink={0} pb={3}>
              <Text fontSize="sm" color="gray.600">
                Pick a clinic to see exactly which schemas belong to it. This reads
                the grouping as it stands in the Assign tab, so unsaved edits show
                here too.
              </Text>
            </Box>

            <Select
              size="sm"
              mb={3}
              value={filterKey}
              onChange={(e) => setFilterKey(e.target.value)}
              aria-label="Filter by group"
            >
              <option value="">All groups ({groups.length})</option>
              {groups.map((g) => (
                <option key={g.key} value={g.key}>
                  {g.name} ({g.schemas.length})
                </option>
              ))}
              {ungrouped.length > 0 && (
                <option value={UNGROUPED_KEY}>
                  Ungrouped ({ungrouped.length})
                </option>
              )}
            </Select>

            <Box
              maxH={{ base: "40vh", md: "45vh" }}
              overflowY="auto"
              pr={2}
              data-testid="schema-group-filter-results"
            >
              {filtered.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  {groups.length === 0
                    ? "No groups yet — every schema stands alone. Assign one in the Assign tab."
                    : "That group has no schemas."}
                </Text>
              ) : (
                <VStack align="stretch" spacing={3}>
                  {filtered.map((entry) => (
                    <Box key={entry.label}>
                      <HStack spacing={2} mb={1.5}>
                        <Badge
                          colorScheme={entry.label === "Ungrouped" ? "gray" : "purple"}
                        >
                          {entry.label}
                        </Badge>
                        <Text fontSize="xs" color="gray.500">
                          {entry.schemas.length} schema
                          {entry.schemas.length === 1 ? "" : "s"}
                        </Text>
                      </HStack>
                      <Table size="sm" variant="simple">
                        <Tbody>
                          {entry.schemas.map((name) => (
                            <Tr key={name}>
                              <Td fontWeight="semibold" py={1.5}>{name}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Box
        flexShrink={0}
        pt={3}
        borderTopWidth="1px"
        borderColor="gray.200"
        bg="white"
        position="sticky"
        bottom={0}
      >
        <Button
          colorScheme="blue"
          w="100%"
          onClick={handleSave}
          isLoading={saving}
          isDisabled={!rows.length}
        >
          Save
        </Button>
      </Box>
    </VStack>
  );
};

export default SchemaGroupAdminSettings;
